// Part 1: Imports and Basic Setup
import React, { useEffect, useState } from "react";
import logo from "../../../assets/logo/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { setCustomers } from "../../../state/reducers/customerSlice";
import { toWords } from "number-to-words";
import Select from "react-select";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-hot-toast";
import { FiEdit, FiTrash } from "react-icons/fi";
import { MdModeEdit } from "react-icons/md";
import ClipLoader from "react-spinners/ClipLoader";
import { IconTrash } from "@tabler/icons-react";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import axios from "axios";

// Import services
import {
  getAbroadCounselingServices,
  getTestPreparationServices,
  getCareerCounselingServices,
  getOtherServices,
  getProfileBuildingServices,
} from "../../../services/user.services";
import { getCustomers } from "../../../services/customer.services";
import { setServices } from "../../../state/reducers/servicesSlice";
import {
  createTremsAndConditions,
  deleteTremsAndConditions,
  getAddress,
  getAllTremsAndConditions,
  sendMail,
  submitInvoice,
  updateAaraDetails,
  updateInvoice,
  updateTremsAndConditions,
} from "../../../services/invoice.services";
import InvoiceTemplate from "./InvoiceTemplate";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { addInvoice } from "../../../state/reducers/invoiceSlice";
import { formatDateTime } from "../../../utils/dateFormat";
import { getNewTable } from "../../../services/mainServices.services";

const formatAmountInWords = (amount) => {
  const roundedAmount = Math.floor(amount);
  const words = toWords(roundedAmount);
  const capitalizedWords =
    words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
  return `Rupees ${capitalizedWords} only`;
};
// Helper functions
const transformInvoiceToNewFormat = (invoice) => {
  const invoiceTotal = parseFloat(invoice.total_amount) || 0;
  const transactions = (invoice.payments || []).map((payment) => {
    const paymentAmount = parseFloat(payment.payment_amount);
    return {
      ...payment,
      payment_amount:
        paymentAmount > 0
          ? paymentAmount
          : invoice.status === "Draft"
          ? invoiceTotal
          : paymentAmount || 0,
    };
  });

  return {
    invoiceId: invoice.invoice_number || "",
    companyDetails: {
      name: invoice.from_address?.name || "Aara Consultancy",
      address: invoice.from_address?.address || "",
      phone: invoice.from_address?.phone || "+91 8108 745 275",
    },
    status: invoice.status || "",
    invoiceDate: invoice.issue_date || "",
    dueDate: invoice.due_date || "",
    details: invoice.items.map((item, index) => ({
      service_name: item.service_name || `Service ${index + 1}`,
      gst: item.gst_percentage || 0,
      rate: item.unit_price || 0,
      amount: item.total_price || 0,
      editing: false, // Default
    })),
    summary: {
      subTotal: invoiceTotal,
      total: invoiceTotal,
      totalPaid: parseFloat(invoice.paid_amount) || 0,
      discount: parseFloat(invoice.discount) || 0,
      amountDue: parseFloat(invoice.pending_amount) || 0,
    },
    amountInWords: formatAmountInWords(invoice.total_amount) || "",
    transactions,
    termsConditions: invoice.terms_conditions || "",
  };
};

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  return isoDate.split("T")[0]; // Extract YYYY-MM-DD
};

function NewInvoice({
  viewInvoice,
  handleCloseInvoice,
  invoice,
  isEditingInvoice = false,
  getAllInvoices,
}) {
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.user.role);
  // State variables
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(
    "Vinni Shah Kotak Bank Account"
  );
  const [discountType, setDiscountType] = useState("percentage");
  const [addresses, setAddresses] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingAndSending, setIsUpdatingAndSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedService, setSelectedService] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedExam, setSelectedExam] = useState("");
  const [selectedCareerService, setSelectedCareerService] = useState("");
  const [selectedOtherService, setSelectedOtherService] = useState("");
  const [selectedProfileBuil, setSelectedProfileBuil] = useState("");
  const [selectedPriceOption, setSelectedPriceOption] = useState("");
  const [isEditingTotalPaid, setIsEditingTotalPaid] = useState(true);
  const [isDiscount, setIsDiscount] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editText, setEditText] = useState("");
  const [termsOptions, setTermsOptions] = useState([]);
  const [selectedTermsOption, setSelectedTermsOption] = useState();
  const [isMoreSelected, setIsMoreSelected] = useState(false);
  const [customTerms, setCustomTerms] = useState("");
  const [coreServices, setCoreServices] = useState([]); // Part 2: Invoice Data and Handlers

  // Initialize invoice data
  const [invoiceData, setInvoiceData] = useState(() => {
    const defaultData = {
      invoiceId: generateUniqueInvoiceId(),
      companyDetails: {
        name: "Aara Consultancy",
        address:
          "608, 6th Floor, Surya House, Road Number 7, opposite R.N Gandhi High School, near Vidyavihar station (east), Rajawadi Colony, Mumbai, Maharashtra 400077",
        phone: "+91 8108 745 275",
      },
      status: "",
      invoiceDate: "",
      dueDate: "",
      details: [],
      summary: {
        subTotal: 0,
        total: 0,
        discount: 0,
        totalPaid: 0,
        amountDue: 0,
      },
      amountInWords: "",
      transactions: [],
      termsConditions: "",
    };

    return isEditingInvoice ? { ...defaultData, ...invoice } : defaultData;
  });

  // Get store data
  const customers = useSelector((state) => state.customer.customers);
  const abroadCounselingServices = useSelector(
    (state) => state.service.abroad_counselling_list
  );
  const testPreparationServices = useSelector(
    (state) => state.service.test_prep_list
  );
  const careerCounselingServices = useSelector(
    (state) => state.service.career_counselling_list
  );
  const otherServices = useSelector(
    (state) => state.service.other_services_list
  );
  const profileBuilding = useSelector(
    (state) => state.service.profile_building_list
  );

  const [editDetails, setEditDetails] = useState({
    ...invoiceData.companyDetails,
  });

  // Validation function
  const validateInvoiceData = () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer.");
      return false;
    }
    if (!invoiceData.status) {
      toast.error("Please select a status.");
      return false;
    }
    if (!selectedPaymentMode) {
      toast.error("Please select a payment mode.");
      return false;
    }
    if (!invoiceData.invoiceDate) {
      toast.error("Please enter an invoice date.");
      return false;
    }
    if (!invoiceData.dueDate) {
      toast.error("Please enter a due date.");
      return false;
    }
    if (!invoiceData.details || invoiceData.details.length === 0) {
      toast.error("Please add at least one service item.");
      return false;
    }
    if (
      invoiceData.summary?.totalPaid === 0 ||
      invoiceData.summary?.totalPaid === null ||
      isNaN(invoiceData.summary?.totalPaid)
    ) {
      toast.error("Please enter a valid Total Paid amount.");
      return false;
    }

    if (!selectedTermsOption && !invoiceData.termsConditions) {
      toast.error("Please select terms and conditions.");
      return false;
    }

    return true;
  };

  // Utility functions
  function generateUniqueInvoiceId() {
    const randomPrefix = "AARA";
    const uniqueNumber = (Date.now() % 1000000).toString().padStart(6, "0");
    return `INV-${randomPrefix}${uniqueNumber}`;
  }

  const generateTransactionReference = () => {
    return `TXN-${uuidv4()}`;
  };

  const getPaymentsForSave = () => {
    const total = invoiceData.summary?.total || 0;
    const totalPaid = invoiceData.summary?.totalPaid || 0;

    const transactionPaid =
      invoiceData.transactions?.reduce(
        (acc, txn) => acc + (Number(txn.payment_amount) || 0),
        0
      ) || 0;

    let payments = [];
    if (invoiceData.transactions?.length > 0) {
      payments = invoiceData.transactions.map((payment) => ({
        transaction_reference:
          payment.transaction_reference || generateTransactionReference(),
        payment_mode: payment.payment_mode || selectedPaymentMode,
        payment_date: payment.payment_date || new Date().toISOString(),
        payment_amount: parseFloat(payment.payment_amount) || 0,
        ...(payment.invoice_url ? { invoice_url: payment.invoice_url } : {}),
      }));

      // If the user entered a higher total paid than the sum of transactions,
      // create a new transaction for the difference.
      if (totalPaid > transactionPaid) {
        payments.push({
          transaction_reference: generateTransactionReference(),
          payment_mode: selectedPaymentMode,
          payment_date: new Date().toISOString(),
          payment_amount: totalPaid - transactionPaid,
        });
      }
    } else {
      if (selectedPaymentMode) {
        payments.push({
          transaction_reference: generateTransactionReference(),
          payment_mode: selectedPaymentMode,
          payment_date: new Date().toISOString(),
          payment_amount: totalPaid > 0 ? totalPaid : total,
        });
      }
    }
    return payments;
  };

  // Customer selection handlers
  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      const data = await response.json();
      dispatch(setCustomers(data));
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      fetchCustomers();
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setInvoiceData((prevData) => ({
      ...prevData,
      recipientName: customer.name,
    }));
    setShowDropdown(false);
  };

  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    label: `${customer.name} (${customer.email})`,
  }));

  const handleChange = (selectedOption) => {
    if (selectedOption) {
      const customer = customers.find((c) => c.id === selectedOption.value);
      setSelectedCustomer(customer);
    } else {
      setSelectedCustomer(null);
    }
  };

  // Company details edit handlers
  const handleEditToggle = async () => {
    if (isEditing && addresses.length > 0) {
      try {
        toast.loading("Updating address...");
        await updateAaraDetails(addresses[0].id, addresses[0]);
        await getAddresses();
        toast.dismiss();
        toast.success("Address updated successfully!");
      } catch (error) {
        console.error("Failed to update address:", error);
        toast.dismiss();
        toast.error("Failed to update address. Please try again.");
        getAddresses();
      }
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedAddress =
      addresses.length > 0
        ? { ...addresses[0], [name]: value }
        : { [name]: value };
    setAddresses([updatedAddress]);
  }; // Part 3: Service Selection and Item Management

  // Service loading effect
  useEffect(() => {
    const loadServices = async () => {
      try {
        if (selectedService === "Abroad Counseling") {
          const data = await getAbroadCounselingServices();
          dispatch(setServices({ abroad_counselling_list: data }));
        } else if (selectedService === "Test Preparation") {
          const data = await getTestPreparationServices();
          dispatch(setServices({ test_prep_list: data }));
        } else if (selectedService === "Career Counseling") {
          const data = await getCareerCounselingServices();
          dispatch(setServices({ career_counselling_list: data }));
        } else if (selectedService === "Other Services") {
          const data = await getOtherServices();
          dispatch(setServices({ other_services_list: data }));
        } else if (selectedService === "Profile Building") {
          const data = await getProfileBuildingServices();
          dispatch(setServices({ profile_building_list: data }));
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    if (selectedService) {
      loadServices();
    }
  }, [selectedService, dispatch]);

  // Service selection handlers
  const handleServiceChange = (event) => {
    setSelectedService(event.target.value);
    setSelectedCountry("");
    setSelectedCourse("");
    setSelectedExam("");
    setSelectedCareerService("");
    setSelectedOtherService("");
    setSelectedProfileBuil("");
    setSelectedPriceOption("");
  };

  // Country and course selection for abroad counseling
  const uniqueCountries = Array.from(
    new Set(abroadCounselingServices.map((s) => s.country))
  );

  const coursesForSelectedCountry = abroadCounselingServices.filter(
    (service) => service.country === selectedCountry
  );

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    setSelectedCourse("");
  };

  const handleCourseChange = (event) => {
    const selectedCourseName = event.target.value;
    setSelectedCourse(selectedCourseName);

    const selectedServiceDetail = abroadCounselingServices.find(
      (service) =>
        service.country === selectedCountry &&
        service.service_name === selectedCourseName
    );
    if (selectedServiceDetail) {
      addServiceItem(
        selectedServiceDetail.service_name,
        selectedServiceDetail.price || 0
      );
    }
  };

  // Test preparation service handlers
  const handleExamChange = (e) => {
    setSelectedExam(e.target.value);
    setSelectedPriceOption("");
  };

  const handlePriceOptionChange = (e) => {
    const option = e.target.value;
    setSelectedPriceOption(option);

    if (option && selectedExam) {
      const service = testPreparationServices.find(
        (s) => s.exam_name === selectedExam
      );

      if (service) {
        let chosenPrice = 0;
        let itemName = service.exam_name;

        if (option === "one") chosenPrice = service.price_for_one;
        if (option === "three") chosenPrice = service.price_for_three;
        if (option === "private") chosenPrice = service.private_coaching_price;

        addServiceItem(itemName, parseFloat(chosenPrice) || 0);
      }
    }
  };

  // Career counseling service handler
  const handleCareerServiceChange = (event) => {
    const careerName = event.target.value;
    setSelectedCareerService(careerName);

    const selectedServiceDetail = careerCounselingServices.find(
      (service) => service.service_name === careerName
    );
    if (selectedServiceDetail) {
      addServiceItem(
        selectedServiceDetail.service_name,
        selectedServiceDetail.price || 0
      );
    }
  };
  const sanitizeHTML = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "s",
        "a",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "pre",
        "code",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
      ALLOW_DATA_ATTR: false,
    });
  };

  // Profile building service handler
  const handleProfileBuildingChange = (event) => {
    const profile = event.target.value;
    setSelectedProfileBuil(profile);
    const selectedServiceDetail = profileBuilding.find(
      (service) => service.service_name === profile
    );
    if (selectedServiceDetail) {
      addServiceItem(
        selectedServiceDetail.service_name,
        selectedServiceDetail.price || 0
      );
    }
  };

  // Other services handler
  const handleOtherServiceChange = (event) => {
    const otherName = event.target.value;
    setSelectedOtherService(otherName);

    const selectedServiceDetail = otherServices.find(
      (service) => service.service_name === otherName
    );
    if (selectedServiceDetail) {
      addServiceItem(
        selectedServiceDetail.service_name,
        selectedServiceDetail.price || 0
      );
    }
  };

  // Item management functions
  const addServiceItem = (itemName, rate) => {
    setInvoiceData((prevData) => ({
      ...prevData,
      details: [
        ...prevData.details,
        {
          service_name: itemName,
          gst:
            selectedPaymentMode === "Vinni Shah Kotak Bank Account"
              ? 18
              : selectedPaymentMode === "Aara Consultancy Business Account"
              ? 18
              : selectedPaymentMode === "Arpan Modani ICICI Bank Account"
              ? 6
              : 0,
          rate: rate,
          amount: rate,
          editing: false,
        },
      ],
    }));
  };

  const addItem = () => {
    setInvoiceData((prevData) => ({
      ...prevData,
      details: [
        ...prevData.details,
        {
          service_name: "",
          gst: 0,
          rate: 0,
          amount: 0,
          editing: true,
        },
      ],
    }));
  };

  const removeItem = (index) => {
    setInvoiceData((prevData) => ({
      ...prevData,
      details: prevData.details.filter((_, i) => i !== index),
    }));
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = invoiceData.details.map((detail, i) => {
      if (i === index) {
        let updatedDetail = { ...detail, [field]: value };

        const rt = parseFloat(updatedDetail.rate) || 0;
        const gstVal = parseFloat(updatedDetail.gst) || 0;

        // Calculate the base rate excluding GST
        const baseRate = updatedDetail.amount / (1 + gstVal / 100) || 0;

        // Calculate the amount including GST
        const gstAmount = baseRate * (gstVal / 100);
        updatedDetail.rate = baseRate.toFixed(2);
        updatedDetail.amount = (baseRate + gstAmount).toFixed(2);

        return updatedDetail;
      }
      return detail;
    });

    setInvoiceData((prevData) => ({
      ...prevData,
      details: updatedDetails,
    }));
  };

  const saveItem = (index) => {
    const updatedDetails = invoiceData.details?.map((detail, i) => {
      if (i === index) {
        return { ...detail, editing: false };
      }
      return detail;
    });
    setInvoiceData((prevData) => ({
      ...prevData,
      details: updatedDetails,
    }));
  };

  const getAddresses = async () => {
    try {
      const res = await getAddress();
      setAddresses(res.data);
    } catch (err) {
      console.error("Failed to fetch addresses:", err);
    }
  };

  useEffect(() => {
    getAddresses();
  }, [viewInvoice]);

  // Calculate base rate (excluding GST) for display
  const calculateBaseRate = (amount, gst) => amount / (1 + gst / 100); // Part 4: Summary Calculations, Terms Handling, and Form Submission

  useEffect(() => {
    const subTotal =
      invoiceData.details?.reduce(
        (acc, curr) => acc + (Number(curr.amount) || 0),
        0
      ) || 0;

    const discount = invoiceData.summary?.discount || 0;

    // Calculate discount amount based on type
    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (subTotal * discount) / 100;
    } else {
      discountAmount = discount; // Direct rupees amount
    }

    const total = subTotal - discountAmount;

    const totalPaid = Number(invoiceData.summary?.totalPaid) || 0;
    const amountDue = total - totalPaid;
    const totalInWords = total > 0 ? formatAmountInWords(total) : "";

    setInvoiceData((prevData) => ({
      ...prevData,
      summary: {
        ...prevData.summary,
        subTotal,
        total,
        amountDue,
      },
      amountInWords: totalInWords,
    }));
  }, [
    invoiceData.details,
    invoiceData.summary?.totalPaid,
    invoiceData.summary?.discount,
    invoiceData.transactions,
    discountType, // Add discountType as dependency
    isEditingInvoice,
    viewInvoice,
  ]);

  // Handle payment and discount changes
  const handleTotalPaidChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setInvoiceData((prevData) => ({
      ...prevData,
      summary: {
        ...prevData.summary,
        totalPaid: value,
      },
    }));
  };

  const handleDiscountPaidChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setInvoiceData((prevData) => ({
      ...prevData,
      summary: {
        ...prevData.summary,
        discount: value,
      },
    }));
  };

  const handleTotalPaidKeyDown = (e) => {
    if (e.key === "Enter") {
      setIsEditingTotalPaid(false);
    }
  };

  const handleDiscountKeyDown = (e) => {
    if (e.key === "Enter") {
      setIsDiscount(false);
    }
  };

  const handleEditButtonClick = () => {
    setIsEditingTotalPaid(true);
  };

  const handleEditButtonClickDiscount = () => {
    setIsDiscount(true);
  };

  // Status and payment mode handlers
  const handleStatusChange = (e) => {
    const status = e.target.value;
    setInvoiceData((prevData) => ({
      ...prevData,
      status: status,
    }));
  };

  const handlePaymentModeChange = (e) => {
    const newPaymentMode = e.target.value;
    setSelectedPaymentMode(newPaymentMode);
    setInvoiceData((prevData) => ({
      ...prevData,
      transactions: prevData.transactions.map((transaction) => ({
        ...transaction,
        payment_mode: newPaymentMode,
      })),
    }));
  };

  // Terms and conditions handlers
  const handleTermsOptionChange = (e) => {
    const selectedId = e.target.value;
    setSelectedTermsOption(selectedId);

    if (selectedId === "more") {
      setIsMoreSelected(true);
      setCustomTerms("");
    } else {
      setIsMoreSelected(false);

      const selectedTerm = termsOptions.find((term) => term.id === selectedId);

      if (selectedTerm) {
        // Store the HTML content as-is, preserving all formatting and links
        setInvoiceData((prevData) => ({
          ...prevData,
          termsConditions: selectedTerm.content,
        }));
      } else {
        setInvoiceData((prevData) => ({
          ...prevData,
          termsConditions: "",
        }));
      }
    }
  };
  const handleCustomTermsChange = (value) => {
    setCustomTerms(value);
    setInvoiceData({ ...invoiceData, termsConditions: value });
  };

  const handleSaveCustomTerms = async () => {
    try {
      if (!customTerms.trim()) {
        toast.error("Please enter valid terms and conditions.");
        return;
      }

      const data = {
        content: customTerms, // Data in the required format
      };

      const response = await createTremsAndConditions(data);
      toast.success("Terms and conditions saved successfully!");

      // Add the newly created term to the options
      setTermsOptions((prev) => [...prev, response]);

      // Reset the state
      setIsMoreSelected("");
      setCustomTerms("");
    } catch (error) {
      console.error("Error creating terms and conditions:", error);
      toast.error("Failed to save terms and conditions.");
    }
  };

  const handleEditTerm = (id) => {
    const selected = termsOptions.find((term) => term.id === parseInt(id));
    if (selected) {
      setEditText(selected.content.replace(/<\/?[^>]+(>|$)/g, ""));
      setIsEditing(true);
    }
  };

  const removeTransaction = (index) => {
    setInvoiceData((prevData) => {
      const removedTxn = prevData.transactions[index];
      const newTransactions = prevData.transactions.filter((_, i) => i !== index);
      const newTotalPaid = Math.max(
        0,
        (Number(prevData.summary.totalPaid) || 0) -
          (Number(removedTxn.payment_amount) || 0)
      );
      return {
        ...prevData,
        transactions: newTransactions,
        summary: {
          ...prevData.summary,
          totalPaid: newTotalPaid,
        },
      };
    });
  };

  const handleDeleteTerm = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this term?"
    );
    if (!confirmDelete) return;
    try {
      await deleteTremsAndConditions(id);
      setTermsOptions((prev) =>
        prev.filter((term) => term.id !== parseInt(id))
      );
      setSelectedTermsOption("");
    } catch (err) {
      toast.error("Failed to delete. Check console for error.");
    }
  };

  const handleSaveEditedTerm = async () => {
    try {
      await updateTremsAndConditions(selectedTermsOption, {
        content: editText,
      });
      const updatedList = termsOptions.map((term) =>
        term.id === parseInt(selectedTermsOption)
          ? { ...term, content: editText }
          : term
      );
      setTermsOptions(updatedList);
      setIsEditing(false);
    } catch (err) {
      toast.error("Failed to update. Check console for error.");
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInvoiceData()) return;
    setIsSubmitting(true); // Start loader

    // Format items with calculated base rate (rate excluding GST)
    const formattedItems = invoiceData.details?.map((item) => {
      const baseRate = item.amount / (1 + item.gst / 100); // Calculate rate excluding GST
      return {
        service_name: item.service_name,
        gst_percentage: item.gst,
        unit_price: baseRate.toFixed(2), // Send rate excluding GST
        total_price: item.amount, // Total price including GST
      };
    });

    const subTotal = invoiceData.summary?.subTotal || 0;
    const discountValue = invoiceData.summary?.discount || 0;

    let discountPercentage = 0;
    let discountInRupees = 0;

    if (discountType === "percentage") {
      discountPercentage = discountValue;
      discountInRupees = (subTotal * discountValue) / 100;
    } else {
      discountInRupees = discountValue;
      discountPercentage = subTotal > 0 ? (discountValue / subTotal) * 100 : 0;
    }

    const data = {
      invoice_number: invoiceData.invoiceId,
      customer_id: selectedCustomer?.id,
      from_address: addresses.id ? addresses.id : 1,
      to_address: selectedCustomer?.address,
      issue_date: invoiceData.invoiceDate,
      due_date: invoiceData.dueDate,
      total_amount: invoiceData.summary?.total,
      paid_amount: invoiceData.summary?.totalPaid,
      pending_amount: invoiceData.summary?.amountDue,
      status: invoiceData.status,
      items: formattedItems, // Pass formatted items with GST-exclusive rate
      amounts_in_words: invoiceData.amountInWords,
      ...(discountType === "percentage"
        ? { discount: discountPercentage }
        : { discount_in_rupees: discountInRupees }),
      payments:
        invoiceData.summary?.totalPaid > 0
          ? [
              {
                transaction_reference: generateTransactionReference(), // Ensure unique reference
                payment_mode: selectedPaymentMode,
                payment_date: new Date().toISOString(),
                payment_amount: invoiceData.summary?.totalPaid || 0,
              },
            ]
          : [],
      terms_conditions: selectedTermsOption || invoiceData.termsConditions,
    };

    try {
      const res = await submitInvoice(data);
      dispatch(addInvoice(res.data));
      toast.success("Invoice submitted successfully!");

      // Reset invoice data
      setInvoiceData({
        invoiceId: generateUniqueInvoiceId(),
        companyDetails: {
          name: "Aara Consultancy",
          address:
            "608, 6th Floor, Surya House, Road Number 7, Vidyavihar, Mumbai",
          phone: "+91 8108 745 275",
        },
        status: "",
        invoiceDate: "",
        dueDate: "",
        details: [],
        summary: {
          subTotal: 0,
          total: 0,
          discount: 0,
          totalPaid: 0,
          amountDue: 0,
        },
        amountInWords: "",
        transactions: [],
        termsConditions: "",
      });
      handleCloseInvoice();
    } catch (err) {
      console.error("Error submitting invoice:", err);
      toast.error("Failed to submit invoice. Please try again.");
    } finally {
      setIsSubmitting(false); // Stop loader
    }
  };

  // Save as draft handler
  const handleSaveAsDraft = async (e) => {
    e.preventDefault();
    const formattedItems = invoiceData.details?.map(
      ({ service_name, gst, rate, amount }) => ({
        service_name,
        gst_percentage: gst,
        unit_price: rate,
        total_price: amount,
      })
    );

    const subTotal = invoiceData.summary?.subTotal || 0;
    const discountValue = invoiceData.summary?.discount || 0;

    let discountPercentage = 0;
    let discountInRupees = 0;

    if (discountType === "percentage") {
      discountPercentage = discountValue;
      discountInRupees = (subTotal * discountValue) / 100;
    } else {
      discountInRupees = discountValue;
      discountPercentage = subTotal > 0 ? (discountValue / subTotal) * 100 : 0;
    }

    const data = {
      invoice_number: invoiceData.invoiceId,
      customer_id: selectedCustomer?.id,
      from_address: addresses.id ? addresses.id : 1,
      to_address: selectedCustomer?.address,
      issue_date: invoiceData.invoiceDate,
      due_date: invoiceData.dueDate,
      total_amount: invoiceData.summary?.total,
      paid_amount: invoiceData.summary?.totalPaid,
      pending_amount: invoiceData.summary?.amountDue,
      status: "Draft", // Explicitly set the status to Draft
      items: formattedItems,
      payments: getPaymentsForSave(),
      ...(discountType === "percentage"
        ? { discount: discountPercentage }
        : { discount_in_rupees: discountInRupees }),
      terms_conditions: selectedTermsOption || invoiceData.termsConditions,
    };

    try {
      if (isEditingInvoice) {
        await updateInvoice(invoice.id, data);
        dispatch(updateInvoice(invoice.id, data));
        setInvoiceData({
          invoiceType: "",
          invoiceId: generateUniqueInvoiceId(),
          companyDetails: {
            name: "Aara Consultancy",
            address:
              "608, 6th Floor, Surya House, Road Number 7, opposite R.N Gandhi High School, near Vidyavihar station (east), Rajawadi Colony, Mumbai, Maharashtra 400077",
            phone: "+91 8108 745 275",
          },
          status: "",
          recipientName: "",
          invoiceDate: "",
          dueDate: "",
          details: [],
          summary: {
            subTotal: 0,
            total: 0,
            discount: "",
            totalPaid: "",
            amountDue: 0,
          },
          amountInWords: "",
          transactions: [],
          termsConditions: "",
        });
      } else {
        const res = await submitInvoice(data);
        dispatch(addInvoice(res.data));
        setInvoiceData({
          invoiceType: "",
          invoiceId: generateUniqueInvoiceId(),
          companyDetails: {
            name: "Aara Consultancy",
            address:
              "608, 6th Floor, Surya House, Road Number 7, opposite R.N Gandhi High School, near Vidyavihar station (east), Rajawadi Colony, Mumbai, Maharashtra 400077",
            phone: "+91 8108 745 275",
          },
          status: "",
          recipientName: "",
          invoiceDate: "",
          dueDate: "",
          details: [],
          summary: {
            subTotal: 0,
            total: 0,
            discount: "",
            totalPaid: "",
            amountDue: 0,
          },
          amountInWords: "",
          transactions: [],
          termsConditions: "",
        });
      }
      handleCloseInvoice();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save draft. Please try again.");
    }
  };

  // Update existing invoice
  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      // Format invoice data for backend
      const formattedItems = invoiceData.details.map(
        ({ service_name, gst, rate, amount }) => ({
          service_name,
          gst_percentage: gst,
          unit_price: rate,
          total_price: amount,
        })
      );

      const subTotal = invoiceData.summary?.subTotal || 0;
      const discountValue = invoiceData.summary?.discount || 0;

      let discountPercentage = 0;
      let discountInRupees = 0;

      if (discountType === "percentage") {
        discountPercentage = discountValue;
        discountInRupees = (subTotal * discountValue) / 100;
      } else {
        discountInRupees = discountValue;
        discountPercentage =
          subTotal > 0 ? (discountValue / subTotal) * 100 : 0;
      }

      const data = {
        invoice_number: invoiceData.invoiceId,
        customer_id: selectedCustomer?.id,
        from_address: addresses.id ? addresses.id : 1,
        to_address: selectedCustomer?.address,
        issue_date: invoiceData.invoiceDate,
        due_date: invoiceData.dueDate,
        total_amount: invoiceData.summary.total,
        paid_amount: invoiceData.summary.totalPaid,
        pending_amount: invoiceData.summary.amountDue,
        status: invoiceData.status,
        items: formattedItems,
        ...(discountType === "percentage"
          ? { discount: discountPercentage }
          : { discount_in_rupees: discountInRupees }),
        terms_conditions: invoiceData.termsConditions,
        payments: getPaymentsForSave(),
      };

      // Update the invoice
      await updateInvoice(invoice.id, data);
      await getAllInvoices();

      toast.success("Invoice updated successfully!");
      handleCloseInvoice();
    } catch (error) {
      console.error("Failed to update invoice:", error);
      toast.error("Failed to update the invoice. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Update and send invoice
  const handleUpdateAndSend = async () => {
    try {
      setIsUpdatingAndSending(true);

      const transactionPaid =
        invoiceData.transactions?.reduce(
          (acc, txn) => acc + (Number(txn.payment_amount) || 0),
          0
        ) || 0;
      const totalPaid = Number(invoiceData.summary.totalPaid) || 0;
      const newPaymentAmount = totalPaid - transactionPaid;

      let updatedTransactions = [...invoiceData.transactions];
      if (newPaymentAmount > 0) {
        const newPayment = {
          transaction_reference: generateTransactionReference(),
          payment_mode: selectedPaymentMode,
          payment_date: new Date().toISOString(),
          payment_amount: newPaymentAmount,
        };
        updatedTransactions.push(newPayment);
      }

      // Format the data to include the updated transactions
      const formattedItems = invoiceData.details.map(
        ({ service_name, gst, rate, amount }) => ({
          service_name,
          gst_percentage: gst,
          unit_price: rate,
          total_price: amount,
        })
      );

      const subTotal = invoiceData.summary?.subTotal || 0;
      const discountValue = invoiceData.summary?.discount || 0;

      let discountPercentage = 0;
      let discountInRupees = 0;

      if (discountType === "percentage") {
        discountPercentage = discountValue;
        discountInRupees = (subTotal * discountValue) / 100;
      } else {
        discountInRupees = discountValue;
        discountPercentage =
          subTotal > 0 ? (discountValue / subTotal) * 100 : 0;
      }

      const data = {
        invoice_number: invoiceData.invoiceId,
        customer_id: selectedCustomer?.id,
        from_address: addresses.id ? addresses.id : 1,
        to_address: selectedCustomer?.address,
        issue_date: invoiceData.invoiceDate,
        due_date: invoiceData.dueDate,
        total_amount: invoiceData.summary.total,
        paid_amount: invoiceData.summary.totalPaid,
        pending_amount: invoiceData.summary.amountDue,
        status: invoiceData.status,
        items: formattedItems,
        ...(discountType === "percentage"
          ? { discount: discountPercentage }
          : { discount_in_rupees: discountInRupees }),
        terms_conditions: selectedTermsOption || invoiceData.termsConditions,
        payments: updatedTransactions,
      };

      // Update the invoice
      await updateInvoice(invoice.id, data);
      await getAllInvoices();
      await sendMail(invoiceData);

      toast.success("Invoice updated and email sent successfully!");
      handleCloseInvoice();
    } catch (error) {
      toast.error("Failed to update and send the invoice. Please try again.");
    } finally {
      setIsUpdatingAndSending(false);
    }
  }; // Part 6: Data Fetching Effects and Initializations

  // Fetch terms and conditions
  useEffect(() => {
    const fetchTermsAndConditions = async () => {
      try {
        const terms = await getAllTremsAndConditions();
        setTermsOptions(terms);
      } catch (error) {
        console.error("Error fetching Terms and Conditions:", error);
      }
    };
    fetchTermsAndConditions();
  }, [viewInvoice]);

  // Fetch core services data
  useEffect(() => {
    const fetchCoreServices = async () => {
      try {
        const data = await getNewTable();
        setCoreServices(data);
      } catch (error) {
        console.error("Failed to fetch core services:", error);
      }
    };

    fetchCoreServices();
  }, [viewInvoice]);

  // Fetch addresses
  useEffect(() => {
    const getAddresses = async () => {
      try {
        const res = await getAddress();
        setAddresses(res.data);
      } catch (err) {
        console.error("Failed to fetch addresses:", err);
      }
    };

    getAddresses();
  }, [viewInvoice]);

  // Initialize invoice data for editing
  useEffect(() => {
    if (isEditingInvoice && invoice) {
      const transformedData = transformInvoiceToNewFormat(invoice);
      setInvoiceData(transformedData);
      setSelectedCustomer(invoice.customer);
      const savedPaymentMode =
        invoice.payments?.[invoice.payments.length - 1]?.payment_mode;
      if (savedPaymentMode) {
        setSelectedPaymentMode(savedPaymentMode);
      }
    }
  }, [invoice, isEditingInvoice, viewInvoice]);

  // Custom selectors and style settings for react-select
  const customStyles = {
    container: (base) => ({
      ...base,
      width: "100%",
      maxWidth: "300px",
    }),
    menu: (base) => ({
      ...base,
      width: "100%",
      maxWidth: "300px",
    }),
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
      {/* Close Button */}
      <div className="flex justify-end mb-4">
        <button
          className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-300"
          onClick={handleCloseInvoice}
        >
          Close
        </button>
      </div>

      {/* Main Invoice Card */}
      <div className="w-full max-w-4xl mx-auto bg-white border shadow-lg p-4 sm:p-8 rounded-lg">
        {/* Header Section */}
        <div className="flex flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
          <div className="flex items-center">
            <img
              src={logo}
              className="w-28 sm:w-40 h-auto object-contain"
              alt="Company Logo"
            />
          </div>
          <div className="text-right">
            <h2 className="text-lg font-bold text-gray-800">INVOICE</h2>
            <p
              className={`text-sm font-bold ${
                invoiceData.status === "Paid"
                  ? "text-green-600"
                  : invoiceData.status === "Pending"
                  ? "text-blue-600"
                  : invoiceData.status === "Partially Paid"
                  ? "text-yellow-600"
                  : invoiceData.status === "Overdue"
                  ? "text-red-600"
                  : "text-gray-600"
              }`}
            >
              {invoiceData.status || "Tax Invoice"}
            </p>
            <p className="text-sm text-gray-600">
              Invoice #: {invoiceData.invoiceId || "INV-12345"}
            </p>
          </div>
        </div>
        {/* Company and Customer Info Section */}
        <div className="flex flex-col md:flex-row justify-between items-start mt-6 pb-6 space-y-4 md:space-y-0">
          {/* Company Info */}
          <div className="w-full md:w-1/2">
            {!isEditing ? (
              <div className="space-y-2">
                <h1 className="text-lg font-bold text-gray-800">
                  Name:{" "}
                  {addresses.length > 0 ? addresses[0]?.name : "Loading..."}
                </h1>
                <p className="text-sm sm:text-base font-medium text-gray-600 max-w-[400px]">
                  <b>Address: </b>
                  {addresses.length > 0 ? addresses[0]?.address : "Loading..."}
                </p>
                <p className="text-sm sm:text-base font-medium text-gray-600 max-w-[400px]">
                  <b>Phone: </b>
                  {addresses.length > 0 ? addresses[0]?.phone : "Loading..."}
                </p>
                <button
                  className="text-blue-500 hover:underline text-sm"
                  onClick={handleEditToggle}
                >
                  Edit
                </button>
              </div>
            ) : (
              <div className="space-y-4 w-full">
                <div className="w-full">
                  <label className="block font-bold text-sm">Name:</label>
                  <input
                    type="text"
                    name="name"
                    value={addresses[0]?.name}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 w-full"
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm">Address:</label>
                  <textarea
                    name="address"
                    value={addresses[0]?.address}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 w-full"
                    rows="4"
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm">Phone:</label>
                  <input
                    type="text"
                    name="phone"
                    value={addresses[0]?.phone}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 w-full"
                  />
                </div>
                <button
                  className="text-blue-500 hover:underline text-sm"
                  onClick={handleEditToggle}
                >
                  Save
                </button>
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="w-full md:w-1/2 md:text-right">
            <p className="text-sm font-bold text-gray-800">Bill To</p>
            {selectedCustomer ? (
              <div>
                <p className="text-sm text-gray-600">
                  {selectedCustomer.name} ({selectedCustomer.email})
                </p>
                <button
                  className="text-blue-500 hover:underline text-sm"
                  onClick={() => setSelectedCustomer(null)}
                >
                  Change Customer
                </button>
              </div>
            ) : (
              <div className="w-full md:flex md:justify-end">
                <Select
                  options={customerOptions}
                  onChange={handleChange}
                  placeholder="+ Add Customer"
                  isClearable
                  styles={customStyles}
                  className="w-full md:w-auto"
                />
              </div>
            )}
          </div>
        </div>
        {/* Invoice Details Fields */}
        <div className="space-y-6 mt-4">
          {/* Status, Payment Mode, Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-600">
                Status:
              </label>
              <select
                className="w-full border border-gray-300 rounded-md p-2"
                value={invoiceData.status}
                onChange={handleStatusChange}
              >
                <option value="">Select Status</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Partially Paid">Partially Paid</option>
                <option value="Paid">Paid</option>
              </select>
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-600">
                Payment Mode:
              </label>
              <select
                value={selectedPaymentMode}
                className="w-full border border-gray-300 rounded-md p-2"
                onChange={handlePaymentModeChange}
              >
                <option value="">Select Payment Mode</option>
                <option value="Vinni Shah Kotak Bank Account">
                  Vinni Shah Kotak Bank Account
                </option>
                <option value="Arpan Modani ICICI Bank Account">
                  Arpan Modani ICICI Bank Account
                </option>
                <option value="Arpan Modani HUF">Arpan Modani HUF</option>
                <option value="Aara Consultancy Business Account">
                  Aara Consultancy Business Account
                </option>
                <option value="Others">Others</option>
              </select>
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-600">
                Invoice Date:
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md p-2"
                value={formatDate(invoiceData.invoiceDate)}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setInvoiceData((prevData) => ({
                    ...prevData,
                    invoiceDate: newDate,
                    dueDate:
                      prevData.status === "Paid" ? newDate : prevData.dueDate,
                  }));
                }}
              />
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-600">
                Due Date:
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md p-2"
                value={formatDate(invoiceData.dueDate)}
                onChange={(e) =>
                  setInvoiceData((prevData) => ({
                    ...prevData,
                    dueDate: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          {/* Service Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-600">
                Service:
              </label>
              <select
                className="w-full border border-gray-300 rounded-md p-2"
                value={selectedService}
                onChange={handleServiceChange}
              >
                <option value="">Select Core Service</option>
                {coreServices.map((service) => (
                  <option key={service.id} value={service.core_service_name}>
                    {service.core_service_name}
                  </option>
                ))}
              </select>
            </div>

            {selectedService === "Abroad Counseling" && (
              <>
                <div className="flex flex-col space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    Country:
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={selectedCountry}
                    onChange={handleCountryChange}
                  >
                    <option value="">Select Country</option>
                    {uniqueCountries.map((country, idx) => (
                      <option key={idx} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    Course:
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={selectedCourse}
                    onChange={handleCourseChange}
                  >
                    <option value="">Select Course</option>
                    {coursesForSelectedCountry.map((service) => (
                      <option key={service.id} value={service.service_name}>
                        {service.service_name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {selectedService === "Test Preparation" && (
              <>
                <div className="flex flex-col space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    Exam Name:
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={selectedExam}
                    onChange={handleExamChange}
                  >
                    <option value="">Select Exam</option>
                    {testPreparationServices.map((service) => (
                      <option key={service.id} value={service.exam_name}>
                        {service.exam_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    Price Details:
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={selectedPriceOption}
                    onChange={handlePriceOptionChange}
                    disabled={!selectedExam}
                  >
                    <option value="">Select Price Option</option>
                    {testPreparationServices.map((service) => {
                      if (service.exam_name === selectedExam) {
                        return (
                          <optgroup key={service.id} label={service.exam_name}>
                            <option value="one">
                              Price for One: {service.price_for_one}
                            </option>
                            <option value="three">
                              Price for Three: {service.price_for_three}
                            </option>
                            <option value="private">
                              Private Coaching: {service.private_coaching_price}
                            </option>
                          </optgroup>
                        );
                      }
                      return null;
                    })}
                  </select>
                </div>
              </>
            )}

            {selectedService === "Career Counseling" && (
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">
                  Name of Service:
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={selectedCareerService}
                  onChange={handleCareerServiceChange}
                >
                  <option value="">Select Service</option>
                  {careerCounselingServices.map((service) => (
                    <option key={service.id} value={service.service_name}>
                      {service.service_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedService === "Other Services" && (
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">
                  Name of Service:
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={selectedOtherService}
                  onChange={handleOtherServiceChange}
                >
                  <option value="">Select Service</option>
                  {otherServices.map((service) => (
                    <option key={service.id} value={service.service_name}>
                      {service.service_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedService === "Profile Building" && (
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">
                  Name of Service:
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={selectedProfileBuil}
                  onChange={handleProfileBuildingChange}
                >
                  <option value="">Select Service</option>
                  {profileBuilding.map((service) => (
                    <option key={service.id} value={service.service_name}>
                      {service.service_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        {/* Items Table */}
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">#</th>
                <th className="border px-4 py-2 text-left">Title</th>
                <th className="border px-4 py-2 text-right">Rate</th>
                <th className="border px-4 py-2 text-right">GST(%)</th>
                <th className="border px-4 py-2 text-right">Amount</th>
                <th className="border px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.details?.map((detail, index) => {
                if (detail.editing) {
                  return (
                    <tr key={index}>
                      <td className="border px-4 py-2">{index + 1}</td>
                      {/* <td className="border px-4 py-2">
                        <input
                          type="text"
                          value={detail.service_name}
                          onChange={(e) =>
                            handleDetailChange(
                              index,
                              "service_name",
                              e.target.value
                            )
                          }
                          className="border p-1 w-full"
                          placeholder="Title"
                        />
                      </td> */}
                      {/* <td className="border px-4 py-2 text-right">
                        <input
                          type="text"
                          value={detail.rate}
                          onChange={(e) =>
                            handleDetailChange(index, "rate", e.target.value)
                          }
                          className="border p-1 w-full text-right"
                          placeholder="Rate"
                        />
                      </td> */}
                      <td className="border px-4 py-2">
                        {detail.service_name}
                      </td>
                      <td className="border px-4 py-2 text-right">
                        Rs.{" "}
                        {calculateBaseRate(
                          Number(detail.amount),
                          Number(detail.gst)
                        ).toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 text-right">
                        <input
                          type="text"
                          value={detail.gst}
                          onChange={(e) =>
                            handleDetailChange(index, "gst", e.target.value)
                          }
                          className="border p-1 w-full text-right"
                          placeholder="GST %"
                        />
                      </td>
                      <td className="border px-4 py-2 text-right">
                        Rs. {(Number(detail.amount) || 0).toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 text-right">
                        <button
                          onClick={() => saveItem(index)}
                          className="text-blue-500 hover:underline"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:underline ml-2"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                } else {
                  return (
                    <tr key={index}>
                      <td className="border px-4 py-2">{index + 1}</td>
                      <td className="border px-4 py-2">
                        {detail.service_name}
                      </td>
                      <td className="border px-4 py-2 text-right">
                        Rs.{" "}
                        {calculateBaseRate(
                          Number(detail.amount),
                          Number(detail.gst)
                        ).toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 text-right">
                        {detail.gst}%
                      </td>
                      <td className="border px-4 py-2 text-right">
                        Rs. {(Number(detail.amount) || 0).toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 text-right">
                        <button
                          onClick={() =>
                            setInvoiceData((prevData) => {
                              const newDetails = [...prevData.details];
                              newDetails[index].editing = true;
                              return { ...prevData, details: newDetails };
                            })
                          }
                          className="text-blue-500 hover:underline mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
          <button
            onClick={addItem}
            className="mt-4 text-green-500 hover:underline text-sm flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add Item
          </button>
        </div>
        {/* Mobile Item List (as cards) - Only visible on small screens */}
        <div className="block sm:hidden mt-6">
          <h3 className="font-medium text-lg mb-2">Invoice Items</h3>
          {invoiceData.details?.map((detail, index) => (
            <div
              key={index}
              className="bg-white border rounded-lg p-4 mb-3 shadow-sm"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="font-medium">{detail.service_name}</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const newDetails = [...invoiceData.details];
                      newDetails[index].editing = true;
                      setInvoiceData({ ...invoiceData, details: newDetails });
                    }}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <FiEdit size={16} />
                  </button>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FiTrash size={16} />
                  </button>
                </div>
              </div>

              {detail.editing ? (
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-gray-500">Title</label>
                    <input
                      type="text"
                      value={detail.service_name}
                      onChange={(e) =>
                        handleDetailChange(
                          index,
                          "service_name",
                          e.target.value
                        )
                      }
                      className="border p-1 w-full mt-1 rounded"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Rate</label>
                      <input
                        type="text"
                        value={detail.rate}
                        onChange={(e) =>
                          handleDetailChange(index, "rate", e.target.value)
                        }
                        className="border p-1 w-full mt-1 text-right rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">GST (%)</label>
                      <input
                        type="text"
                        value={detail.gst}
                        onChange={(e) =>
                          handleDetailChange(index, "gst", e.target.value)
                        }
                        className="border p-1 w-full mt-1 text-right rounded"
                      />
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Amount:</span>
                    <span>Rs. {(Number(detail.amount) || 0).toFixed(2)}</span>
                  </div>
                  <button
                    onClick={() => saveItem(index)}
                    className="w-full text-center bg-blue-500 text-white py-1 px-3 rounded mt-2"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 text-sm">
                    <span className="text-gray-500">Base Rate:</span>
                    <span className="text-right">
                      Rs.{" "}
                      {calculateBaseRate(
                        Number(detail.amount),
                        Number(detail.gst)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 text-sm">
                    <span className="text-gray-500">GST:</span>
                    <span className="text-right">{detail.gst}%</span>
                  </div>
                  <div className="grid grid-cols-2 text-sm font-medium">
                    <span>Total Amount:</span>
                    <span className="text-right">
                      Rs. {(Number(detail.amount) || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
          <button
            onClick={addItem}
            className="w-full text-center bg-green-500 text-white py-2 rounded-lg flex items-center justify-center mt-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add New Item
          </button>
        </div>
        {/* Summary Section */}
        <div className="mt-10 flex justify-end">
          <div className="w-full max-w-md">
            <div className="flex justify-between border-b py-2">
              <p className="text-sm font-bold text-gray-800">Sub Total</p>
              <p className="text-sm text-gray-600">
                Rs. {invoiceData.summary?.subTotal.toFixed(2)}
              </p>
            </div>
            <div className="flex justify-between border-b py-2 bg-gray-100">
              <p className="text-sm font-bold text-gray-800">Total</p>
              <p className="text-sm text-gray-600">
                Rs. {invoiceData.summary?.total.toFixed(2)}
              </p>
            </div>
            <div className="flex justify-between border-b py-2 items-center">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-bold text-gray-800">Discount</p>
                <select
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  className="text-xs border border-gray-300 rounded px-1 py-0.5"
                >
                  <option value="percentage">%</option>
                  <option value="rupees">₹</option>
                </select>
              </div>
              <div className="text-sm text-gray-600 flex items-center">
                {isDiscount ? (
                  <input
                    type="text"
                    className="border border-gray-300 rounded-md p-1 text-right w-24"
                    value={invoiceData.summary?.discount || ""}
                    onChange={handleDiscountPaidChange}
                    onKeyDown={handleDiscountKeyDown}
                    placeholder={`Enter discount ${
                      discountType === "percentage" ? "%" : "₹"
                    }`}
                  />
                ) : (
                  <>
                    <span>
                      {invoiceData.summary?.discount
                        ? discountType === "percentage"
                          ? `${invoiceData.summary?.discount.toFixed(2)}%`
                          : `₹${invoiceData.summary?.discount.toFixed(2)}`
                        : ""}
                    </span>
                    <button
                      className="text-blue-500 hover:underline ml-2 p-1 text-xs"
                      onClick={handleEditButtonClickDiscount}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between border-b py-2 items-center">
              <p className="text-sm font-bold text-gray-800">Total Paid</p>
              <div className="text-sm text-gray-600 flex items-center">
                {isEditingTotalPaid ? (
                  <input
                    type="text"
                    className="border border-gray-300 rounded-md p-1 text-right w-24"
                    value={invoiceData.summary?.totalPaid || ""}
                    onChange={handleTotalPaidChange}
                    onKeyDown={handleTotalPaidKeyDown}
                    placeholder="Enter total paid"
                  />
                ) : (
                  <>
                    <span>
                      {invoiceData.summary?.totalPaid
                        ? `Rs. ${invoiceData.summary?.totalPaid.toFixed(2)}`
                        : ""}
                    </span>
                    <button
                      className="text-blue-500 hover:underline ml-2 p-1 text-xs"
                      onClick={handleEditButtonClick}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between py-2 bg-gray-100">
              <p className="text-sm font-bold text-gray-800">Amount Due</p>
              <p className="text-sm text-gray-600">
                Rs. {invoiceData.summary?.amountDue.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        {/* Amount in Words */}
        <div className="mt-6">
          <p className="text-sm text-gray-800 text-center">
            <span className="font-bold">With words:</span>{" "}
            {invoiceData.amountInWords}
          </p>
        </div>
        {/* Transactions Table */}
        {invoiceData.transactions && invoiceData.transactions.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <p className="text-sm font-bold text-gray-800 mb-2">
              Transactions:
            </p>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left">Payment #</th>
                  <th className="px-4 py-2 text-left">Payment Mode</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.transactions?.map((transaction, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2">
                      {transaction.transaction_reference}
                      <div className="flex items-center">
                        {/* {transaction.invoice_url && ( */}
                        <a
                          href={
                            transaction.invoice_url ||
                            `${process.env.REACT_APP_BASE_URL}/media/invoices/${transaction.transaction_reference}.pdf`
                          }
                          download
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Download Invoice"
                        >
                          <button
                            type="button"
                            className="ml-2 bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs"
                          >
                            Download
                          </button>
                        </a>
                        {/* )} */}
                        {userRole === "superadmin" && (
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => removeTransaction(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-2">{transaction.payment_mode}</td>
                    <td className="px-4 py-2">
                      {formatDateTime(transaction.payment_date)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      Rs. {Number(transaction.payment_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Mobile Transaction Cards - Only visible on small screens */}
        {invoiceData.transactions && invoiceData.transactions.length > 0 && (
          <div className="block sm:hidden mt-6">
            <p className="text-sm font-bold text-gray-800 mb-2">
              Transactions:
            </p>
            {invoiceData.transactions.map((transaction, index) => (
              <div
                key={index}
                className="bg-white border rounded-lg p-4 mb-3 shadow-sm"
              >
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Payment #:</span>
                    <span className="text-sm font-medium">
                      {transaction.transaction_reference}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Payment Mode:</span>
                    <span className="text-sm">{transaction.payment_mode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Date:</span>
                    <span className="text-sm">
                      {formatDateTime(transaction.payment_date)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Amount:</span>
                    <span className="text-sm font-medium">
                      Rs. {Number(transaction.payment_amount || 0).toFixed(2)}
                    </span>
                  </div>
                  {/* {transaction.invoice_url && ( */}
                  <a
                    href={
                      transaction.invoice_url ||
                      `${process.env.REACT_APP_BASE_URL}/media/invoices/${transaction.transaction_reference}.pdf`
                    }
                    download
                    className="block w-full mt-2"
                  >
                    <button
                      type="button"
                      className="w-full bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-700 text-sm"
                    >
                      Download Invoice
                    </button>
                  </a>
                  {/* )} */}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Terms & Conditions */}
        <div className="mt-8">
          <p className="text-lg font-bold text-gray-800 mb-3">
            Terms & Conditions
          </p>

          <div className="flex flex-col md:flex-row gap-3">
            <select
              className="border border-gray-300 rounded-md p-2 w-full md:w-1/2"
              value={selectedTermsOption}
              onChange={handleTermsOptionChange}
            >
              <option value="" className="w-full">
                Select Terms & Conditions
              </option>
              {termsOptions.map((term, index) => {
                // Remove HTML tags and split the content into words
                const text = term.content.replace(/<\/?[^>]+(>|$)/g, "").trim();
                const words = text.split(/\s+/); // Split by any whitespace character

                // Get the first seven words
                const firstFiveWords = words.slice(0, 7).join(" ");

                return (
                  <option key={index} value={term.id} className="w-full">
                    {firstFiveWords}...
                  </option>
                );
              })}
              <option value="more" className="w-full">
                More...
              </option>
            </select>

            {selectedTermsOption && selectedTermsOption !== "more" && (
              <div className="flex space-x-2">
                <button
                  className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex items-center"
                  onClick={() => handleEditTerm(selectedTermsOption)}
                >
                  <MdModeEdit className="mr-1" /> Edit
                </button>
                <button
                  className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm flex items-center"
                  onClick={() => handleDeleteTerm(selectedTermsOption)}
                >
                  <IconTrash size={16} className="mr-1" /> Delete
                </button>
              </div>
            )}
          </div>

          {isEditing && (
            <div className="mt-4">
              <ReactQuill
                theme="snow"
                value={editText}
                onChange={(value) => setEditText(value)}
                placeholder="Enter your custom terms and conditions"
                className="h-40 mb-12" // Extra margin to account for the Quill toolbar
              />

              <div className="mt-12 flex space-x-2">
                {" "}
                {/* Extra margin to position below the Quill editor */}
                <button
                  className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                  onClick={handleSaveEditedTerm}
                >
                  Save Changes
                </button>
                <button
                  className="px-3 py-1.5 bg-gray-300 rounded hover:bg-gray-400"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isMoreSelected && (
            <div className="mt-4">
              <ReactQuill
                theme="snow"
                value={customTerms}
                onChange={handleCustomTermsChange}
                placeholder="Enter your custom terms and conditions"
                className="h-40 mb-12" // Extra margin for the toolbar
              />
              <div className="mt-12">
                {" "}
                {/* Extra margin to position below the Quill editor */}
                <button
                  className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={handleSaveCustomTerms}
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {/* Display the selected terms or custom terms */}
          <div className="mt-4 p-4 border rounded-lg bg-gray-50">
            {selectedTermsOption === "more" ? (
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(customTerms || "No terms provided."),
                }}
              />
            ) : (
              selectedTermsOption && (
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(
                      termsOptions.find(
                        (item) => item.id === parseInt(selectedTermsOption)
                      )?.content || "No terms selected."
                    ),
                  }}
                />
              )
            )}
          </div>
        </div>
        {/* Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-between">
          {!isEditingInvoice && (
            <button
              className="px-6 py-2 bg-gray-200 rounded-md hover:bg-gray-300 w-full sm:w-auto"
              onClick={handleSaveAsDraft}
            >
              Save as Draft
            </button>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            {isEditingInvoice ? (
              <>
                <button
                  className={`px-6 py-2 rounded-md w-full sm:w-auto ${
                    isUpdating
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                  onClick={handleUpdate}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <ClipLoader color="white" size={20} />
                  ) : (
                    "Update"
                  )}
                </button>
                <button
                  className={`px-6 py-2 rounded-md w-full sm:w-auto ${
                    isUpdatingAndSending
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                  onClick={handleUpdateAndSend}
                  disabled={isUpdatingAndSending}
                >
                  {isUpdatingAndSending ? (
                    <ClipLoader color="white" size={20} />
                  ) : (
                    "Update & Send"
                  )}
                </button>
              </>
            ) : (
              <button
                className={`px-6 py-2 rounded-md w-full sm:w-auto ${
                  isSubmitting
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ClipLoader color="white" size={20} />
                ) : (
                  "Create & Send"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewInvoice;
